import React, { useEffect, useMemo, useState } from "react";
import { api, rupiah, formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileText, FileSpreadsheet, ArrowDownCircle, ArrowUpCircle, PiggyBank, BarChart3 } from "lucide-react";
import { usePaged, Pager } from "@/components/Pager";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const todayStr = () => new Date().toISOString().slice(0, 10);
const MONTHS_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const mLabel = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS_SHORT[Number(m)]} ${y}` : p; };

export default function AdminLedgerReport({ category, title, subtitle, setoranLabel = "Setoran", penarikanLabel = "Penarikan", buyerLabel = "Nama Santri", idLabel = "NISN", withPending = false, showChart = false }) {
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(todayStr());
  const [source, setSource] = useState("all");
  const [data, setData] = useState({ rows: [], sources: [], total_setoran: 0, total_penarikan: 0 });
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);

  const load = () => {
    setLoading(true);
    const params = { category };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (source !== "all") params.source = source;
    api.get("/admin/ledger-report", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadWd = () => { if (withPending) api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {}); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [category, dateFrom, dateTo, source]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadWd(); }, [category]);

  const dl = async (format) => {
    try {
      const params = new URLSearchParams({ category, format });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (source !== "all") params.set("source", source);
      const r = await api.get(`/admin/ledger-report/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${category === "tabungan" ? "rekap_tabungan" : "rekap_saldo_belanja"}.${format === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh laporan"); }
  };

  const decide = async (id, action) => {
    try {
      await api.post(`/admin/withdrawals/${id}/${action}`);
      toast.success(action === "approve" ? "Penarikan disetujui" : "Penarikan ditolak");
      loadWd(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pending = withdrawals.filter((w) => w.status === "pending");
  const pg = usePaged(data.rows, 15);

  const chartData = useMemo(() => {
    const m = {};
    data.rows.forEach((r) => {
      const key = (r.date || "").slice(0, 7);
      if (!key) return;
      const o = m[key] || (m[key] = { month: key, setoran: 0, penarikan: 0 });
      o.setoran += r.setoran; o.penarikan += r.penarikan;
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month)).map((o) => ({ ...o, label: mLabel(o.month) }));
  }, [data.rows]);

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => dl("pdf")} data-testid="ledger-pdf" className="rounded-xl"><FileText size={16} className="mr-1.5" /> PDF</Button>
            <Button variant="outline" onClick={() => dl("xlsx")} data-testid="ledger-xlsx" className="rounded-xl"><FileSpreadsheet size={16} className="mr-1.5" /> Excel</Button>
          </div>
        }
      />

      {withPending && pending.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 font-heading font-semibold text-amber-800">
            Permintaan Penarikan Tabungan ({pending.length})
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Santri</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((w) => (
                <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                  <TableCell className="font-medium text-stone-800">{w.student_name}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(w.amount)}</TableCell>
                  <TableCell className="text-sm text-stone-500">{w.note || "-"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => decide(w.id, "approve")} data-testid={`approve-${w.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">Setujui</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(w.id, "reject")} data-testid={`reject-${w.id}`} className="ml-1 rounded-lg text-rose-600 border-rose-200">Tolak</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Dari Tanggal</label>
          <DateField value={dateFrom} onChange={setDateFrom} testid="ledger-date-from" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Sampai Tanggal</label>
          <DateField value={dateTo} onChange={setDateTo} testid="ledger-date-to" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Petugas</label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="rounded-xl bg-white h-12" data-testid="ledger-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Petugas</SelectItem>
              {data.sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-emerald-600 text-white rounded-2xl p-3">
            <p className="text-emerald-100 text-xs">{setoranLabel}</p>
            <p className="font-heading text-lg font-bold mt-1" data-testid="total-setoran">{rupiah(data.total_setoran)}</p>
          </div>
          <div className="bg-rose-500 text-white rounded-2xl p-3">
            <p className="text-rose-100 text-xs">{penarikanLabel}</p>
            <p className="font-heading text-lg font-bold mt-1" data-testid="total-penarikan">{rupiah(data.total_penarikan)}</p>
          </div>
        </div>
      </div>

      {showChart && chartData.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-5 mb-5" data-testid="ledger-chart">
          <p className="font-heading font-semibold text-stone-900 flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-emerald-600" /> {setoranLabel} vs {penarikanLabel} per Bulan</p>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0efe9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#78716c" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(0)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)} tick={{ fontSize: 11, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={44} />
                <Tooltip formatter={(v) => rupiah(v)} cursor={{ fill: "#f5f5f4" }} contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey="setoran" name={setoranLabel} fill="#059669" radius={[6, 6, 0, 0]} maxBarSize={46} />
                <Bar dataKey="penarikan" name={penarikanLabel} fill="#f43f5e" radius={[6, 6, 0, 0]} maxBarSize={46} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : data.rows.length === 0 ? (
          <Empty><PiggyBank className="mx-auto mb-2" /> Tidak ada mutasi pada rentang tanggal ini.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Tanggal & Jam</TableHead>
                <TableHead>{buyerLabel}</TableHead>
                <TableHead>{idLabel}</TableHead>
                <TableHead className="text-right">{setoranLabel}</TableHead>
                <TableHead className="text-right">{penarikanLabel}</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Petugas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((r, i) => (
                <TableRow key={i} data-testid={`ledger-row-${i}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.date)}</TableCell>
                  <TableCell className="font-medium">{r.student}</TableCell>
                  <TableCell className="font-mono text-sm text-stone-500">{r.nisn}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-600">
                    {r.setoran > 0 ? <span className="inline-flex items-center gap-1 justify-end"><ArrowDownCircle size={13} /> {rupiah(r.setoran)}</span> : "-"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-rose-600">
                    {r.penarikan > 0 ? <span className="inline-flex items-center gap-1 justify-end"><ArrowUpCircle size={13} /> {rupiah(r.penarikan)}</span> : "-"}
                  </TableCell>
                  <TableCell className={`text-right font-mono text-sm font-bold ${r.saldo < 0 ? "text-rose-600" : ""}`}>
                    {r.saldo < 0 ? `-${rupiah(Math.abs(r.saldo))}` : rupiah(r.saldo)}
                  </TableCell>
                  <TableCell className="text-sm text-stone-500">{r.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {!loading && data.rows.length > 0 && <Pager {...pg} />}
    </div>
  );
}
